import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Dialog, Button, LinearProgress, IconButton } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { IconUpload, IconX } from "@tabler/icons-react";

import { uploadResumeAPI, fetchResumeStatusAPI } from "../../../services/resumeService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import tokens from "../../theme/tokens";
import {
  PHASE,
  initialFiles,
  withUploadPercent,
  toProcessing,
  withServerStatuses,
  allFailed,
  isSettled,
  isFullyScored,
  statusLabel,
} from "./uploadMachine";

const { color, font, radius, shadow } = tokens;

const MAX_FILES = 50;
const POLL_MS = 3000;

/**
 * Upload is a modal over the console, not a separate page — and closing it is now genuinely safe.
 * The server owns the pipeline end to end (a row exists from the moment the PDF lands in S3, and
 * the worker chains parse -> score itself), so nothing depends on this component, or any browser,
 * staying alive. The candidates table shows the same rows, and survives a refresh.
 *
 * That is why this no longer touches uploadContext. The old modal borrowed its socket connection
 * and its single shared progress bar, and that bar was the bug: it filled 0->100% for the S3
 * upload and was then reset to 0 so the parse could reuse it, which read as the upload undoing
 * itself. Here each file owns its own row, and progress only ever moves forwards — determinate
 * while bytes are in flight, indeterminate once the server takes over, because at that point a
 * percentage would be a number we do not have.
 *
 * uploadContext is left alone deliberately: the legacy Application.js still drives its upload
 * through it, and this change has no business breaking that.
 */
const UploadModal = ({ open, onClose, jobId, jobTitle, onComplete }) => {
  const dispatch = useDispatch();

  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Read inside the poll without making it a dependency — re-creating the interval on every
  // status tick would reset the clock and, with a short enough response, never fire again.
  const filesRef = useRef(files);
  filesRef.current = files;

  const settled = files.length > 0 && isSettled(files);
  const scored = isFullyScored(files);

  // Poll the server for as long as anything can still change. Polling (not the socket) is what
  // makes this survive a refresh: the state lives in the database, so there is nothing to lose.
  useEffect(() => {
    if (!open || !jobId || !busy || settled) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const statuses = await fetchResumeStatusAPI(jobId);
        if (cancelled) return;
        setFiles((prev) => withServerStatuses(prev, statuses));
      } catch (e) {
        // A failed poll is not a failed upload — the work continues server-side regardless. Stay
        // quiet and try again; surfacing this would tell the user their resumes are in trouble
        // when the only thing in trouble is one HTTP request.
        console.error("Status poll failed:", e);
      }
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [open, jobId, busy, settled]);

  // Everything finished — refresh the table behind the modal so it agrees with what this says.
  useEffect(() => {
    if (settled && busy) {
      setBusy(false);
      onComplete?.();
    }
  }, [settled, busy, onComplete]);

  const onDrop = useCallback(
    async (accepted) => {
      setError("");
      if (!jobId) {
        setError("No job selected.");
        return;
      }

      const pdfs = accepted.filter((f) => f.type === "application/pdf");
      if (!pdfs.length) {
        setError("PDF resumes only.");
        return;
      }
      if (pdfs.length !== accepted.length) {
        setError("Some files weren't PDFs and were skipped.");
      }
      if (pdfs.length > MAX_FILES) {
        setError(`Up to ${MAX_FILES} files at once.`);
        return;
      }

      setFiles(initialFiles(pdfs));
      setBusy(true);

      try {
        await uploadResumeAPI(jobId, pdfs, (percent) =>
          setFiles((prev) => withUploadPercent(prev, percent))
        );
        // No reset to zero. The bytes are in S3; the row switches to an indeterminate bar rather
        // than restarting a determinate one at 0.
        setFiles((prev) => toProcessing(prev));
        dispatch(
          showAppAlert({
            show: true,
            alertType: "success",
            alertMessage: "Upload complete — processing continues in the background.",
          })
        );
      } catch (e) {
        setError(e.message || "Upload failed.");
        setFiles((prev) => allFailed(prev, e.message));
        setBusy(false);
      }
    },
    [jobId, dispatch]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: true,
  });

  const handleClose = () => {
    onClose();
    onComplete?.();
  };

  const toneOf = (f) => {
    if (f.phase === PHASE.FAILED) return color.danger;
    if (f.phase === PHASE.DONE) return color.success;
    return color.textMuted;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: 560,
          maxWidth: "100%",
          borderRadius: `${radius.modal}px`,
          boxShadow: shadow.modal,
        },
      }}
      BackdropProps={{ sx: { bgcolor: "rgba(23,23,31,.4)" } }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", p: 2.5, pb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ font: `600 15px ${font.sans}`, color: color.text }}>Upload resumes</Box>
          <Box sx={{ mt: 0.25, font: `400 11.5px ${font.sans}`, color: color.textMuted }}>
            {jobTitle || "Current job"}
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <IconX size={16} stroke={1.75} color={color.textMuted} />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Box
          {...getRootProps()}
          sx={{
            border: `1.5px dashed ${isDragActive ? color.indigo : "#C9C9D4"}`,
            borderRadius: `${radius.fileRow + 2}px`,
            bgcolor: isDragActive ? color.indigoTint : color.bg,
            p: 4.5,
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <Box
            sx={{
              width: 40,
              height: 40,
              mx: "auto",
              mb: 1.5,
              display: "grid",
              placeItems: "center",
              borderRadius: `${radius.fileRow}px`,
              bgcolor: color.indigoTint,
            }}
          >
            <IconUpload size={18} stroke={1.75} color={color.indigo} />
          </Box>
          <Box sx={{ font: `600 14px ${font.sans}`, color: color.text }}>
            Drop PDF resumes here
          </Box>
          <Box sx={{ mt: 0.5, font: `400 12px ${font.sans}`, color: color.textMuted }}>
            or <b style={{ color: color.indigo }}>browse files</b> · PDF only, up to{" "}
            {MAX_FILES} at once
          </Box>
        </Box>

        {error && (
          <Box sx={{ mt: 1.5, font: `400 11.5px ${font.sans}`, color: color.danger }}>
            {error}
          </Box>
        )}

        {files.length > 0 && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {files.map((f) => (
              <Box
                key={f.name}
                sx={{
                  border: `1px solid ${color.border}`,
                  borderRadius: `${radius.fileRow}px`,
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      font: `500 11px ${font.mono}`,
                      color: color.textMuted,
                      border: `1px solid ${color.border}`,
                      borderRadius: `${radius.chip}px`,
                      px: 0.625,
                    }}
                  >
                    PDF
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      font: `500 12.5px ${font.sans}`,
                      color: color.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.name}
                  </Box>
                  <Box sx={{ font: `400 11.5px ${font.mono}`, color: toneOf(f) }}>
                    {statusLabel(f)}
                  </Box>
                </Box>

                {/* Determinate while we are counting bytes; indeterminate once the server has
                    them, because from there we genuinely do not know how far along it is. The one
                    thing this must never do is go backwards. */}
                {(f.phase === PHASE.UPLOADING || f.phase === PHASE.PROCESSING) && (
                  <LinearProgress
                    variant={f.phase === PHASE.UPLOADING ? "determinate" : "indeterminate"}
                    value={f.phase === PHASE.UPLOADING ? f.percent : undefined}
                    sx={{
                      mt: 1,
                      height: 4,
                      borderRadius: 3,
                      bgcolor: color.borderRow,
                      "& .MuiLinearProgress-bar": { bgcolor: color.indigo },
                    }}
                  />
                )}

                {f.phase === PHASE.FAILED && f.error && (
                  <Box sx={{ mt: 0.75, font: `400 11px ${font.sans}`, color: color.danger }}>
                    {f.error}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {files.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              font: `400 11px ${font.mono}`,
              color: color.textMuted,
              textAlign: "right",
            }}
          >
            {settled
              ? "All done."
              : "You can close this — processing continues, and the table will show it."}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 2.5,
          py: 2,
          bgcolor: color.bg,
          borderTop: `1px solid ${color.border}`,
        }}
      >
        <Button variant="outlined" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={!scored}
          onClick={() => {
            onComplete?.();
            onClose();
          }}
        >
          View rankings →
        </Button>
      </Box>
    </Dialog>
  );
};

export default UploadModal;
